import { calculateExerciseProgress } from './analyzers/ProgressAnalyzer';
import { generateTrainingSuggestions, generateMuscleBalanceSuggestions, generatePeriodizationSuggestions } from './analyzers/SuggestionGenerator';
import { analyzeRecoveryAndOvertraining } from './analyzers/RecoveryAnalyzer';
import { 
  filterWorkoutsByTimeRange, 
  calculateWorkoutStats, 
  analyzeMuscleTrained, 
  analyzeExerciseDistribution 
} from './analyzers/WorkoutAnalyzer';
import { identifyTrainingGaps } from './analyzers/ProgramAnalyzer';
import { generateSessionInsights } from './analyzers/SessionAnalyzer';
import { analyzeLoadDistribution } from './analyzers/LoadAnalyzer';
import { calculate1RM, calculateWeightRange } from './utils/ExerciseUtils';
import { MLAnalyzer } from './analyzers/MLAnalyzer';
import { preprocessTrainingData } from './utils/MachineLearningUtils';
import { MLDebugger } from './utils/MLDebugger';
import { MLMonitor } from './utils/MLMonitor';
import { debugLog } from '../../utils/logger';

/**
 * TrainingAnalyzer - Servizio centralizzato per l'analisi dei dati di allenamento
 * Versione modulare che delega le funzionalità ai moduli specifici
 * Ora con integrazione ML per analisi predittiva
 */
class TrainingAnalyzer {
  static calculate1RM(weight, reps) {
    return calculate1RM(weight, reps);
  }

  static calculateWeightRange(currentWeight, exerciseType = 'general', options = {}) {
    return calculateWeightRange(currentWeight, exerciseType, options);
  }

  static filterWorkoutsByTimeRange(workouts, timeRange = 'month') {
    return filterWorkoutsByTimeRange(workouts, timeRange);
  }

  static analyzeExerciseProgress(sets) {
    return calculateExerciseProgress(sets, this.calculate1RM, this.calculateWeightRange);
  }

  static generateTrainingSuggestions(workouts, sets) {
    return generateTrainingSuggestions(workouts, sets, this);
  }

  static analyzeLoadDistribution(sets) {
    return analyzeLoadDistribution(sets);
  }

  static analyzeRecoveryAndOvertraining(workouts, sets) {
    return analyzeRecoveryAndOvertraining(workouts, sets, this);
  }

  static generateMuscleBalanceSuggestions(sets) {
    return generateMuscleBalanceSuggestions(sets);
  }

  static generatePeriodizationSuggestions(workouts, sets) {
    return generatePeriodizationSuggestions(workouts, sets);
  }

  static processBatchedData(sets, batchSize = 100, processFn) {
    if (!sets || !sets.length || !processFn) return [];

    const results = [];
    for (let i = 0; i < sets.length; i += batchSize) {
      const batch = sets.slice(i, i + batchSize);
      const batchResults = processFn(batch);
      results.push(...batchResults);
    }

    return results;
  }

  static generatePersonalizedTrainingSuggestions(workouts, sets, userProfile = {}) {
    const baseSuggestions = this.generateTrainingSuggestions(workouts, sets);
    const experienceLevel = userProfile.experienceLevel || 'intermediate';

    const personalizedSuggestions = baseSuggestions.map(suggestion => {
      const personalizedSuggestion = { ...suggestion };
      personalizedSuggestion.shouldShowTip = true;
      personalizedSuggestion.actionable = true;

      if (experienceLevel === 'beginner' && !personalizedSuggestion.suggestedAction) {
        if (suggestion.type === 'plateau') {
          personalizedSuggestion.suggestedAction = "Aumenta il peso del 5% o le ripetizioni di 2 nel prossimo allenamento";
        } else if (suggestion.type === 'progress') {
          personalizedSuggestion.suggestedAction = "Mantieni questa progressione con incrementi graduali";
        }
      }

      return personalizedSuggestion;
    });

    const recoverySuggestions = this.analyzeRecoveryAndOvertraining(workouts, sets);
    const periodizationSuggestions = this.generatePeriodizationSuggestions(workouts, sets);

    return [...personalizedSuggestions, ...recoverySuggestions, ...periodizationSuggestions];
  }

  static identifyTrainingGaps(workouts, sets, userProfile = {}) {
    return identifyTrainingGaps(workouts, sets, userProfile);
  }

  static generateSessionInsights(sessionSets, historicalSets) {
    return generateSessionInsights(sessionSets, historicalSets, this);
  }

  static calculateMuscleGroupFatigue(sets, muscleGroup) {
    const muscleGroupSets = sets.filter(set => set.gruppo_muscolare === muscleGroup);
    if (muscleGroupSets.length === 0) return 0;

    const totalVolume = muscleGroupSets.reduce((sum, set) => {
      const reps = parseInt(set.ripetizioni) || 0;
      const weight = parseFloat(set.peso) || 0;
      return sum + (reps * weight);
    }, 0);

    let intensitySum = 0;
    let validSets = 0;

    muscleGroupSets.forEach(set => {
      const reps = parseInt(set.ripetizioni) || 0;
      const weight = parseFloat(set.peso) || 0;
      if (reps > 0 && weight > 0) {
        const estimatedRM = this.calculate1RM(weight, reps);
        if (estimatedRM > 0) {
          const intensity = weight / estimatedRM;
          intensitySum += intensity;
          validSets++;
        }
      }
    });

    const avgIntensity = validSets > 0 ? intensitySum / validSets : 0;

    const timestamps = muscleGroupSets
      .map(set => new Date(set.timestamp || set.data_allenamento).getTime())
      .sort();

    let densityFactor = 0;
    if (timestamps.length > 1) {
      const timeSpan = timestamps[timestamps.length - 1] - timestamps[0];
      const avgTimeBetweenSets = timeSpan / (timestamps.length - 1);
      densityFactor = 10000 / (avgTimeBetweenSets + 1);
      densityFactor = Math.min(5, densityFactor);
    }

    const volumeFactor = Math.min(5, totalVolume / 1000);
    const intensityFactor = avgIntensity * 10;

    return Math.min(10, Math.round(
      (volumeFactor * 0.4) + 
      (intensityFactor * 0.4) + 
      (densityFactor * 0.2)
    ));
  }

  static async initializeMLAnalyzer(workouts, sets, userProfile = {}) {
    if (!workouts || workouts.length < 10 || !sets || sets.length < 50) {
      console.warn('Dati insufficienti per l\'analisi ML');
      return null;
    }
  
    try {
      const initFunction = async (workouts, sets, userProfile) => {
        const mlAnalyzer = new MLAnalyzer({ workouts, sets }, userProfile);
        await mlAnalyzer.initialize();
        return mlAnalyzer;
      };
      
      // Utilizza MLDebugger per monitorare l'inizializzazione
      return await MLDebugger.monitorMLInitialization(
        initFunction, 
        { workouts, sets }, 
        userProfile
      );
    } catch (error) {
      console.error('Errore nell\'inizializzazione dell\'analisi ML:', error);
      return null;
    }
  }

  static async generateMLSuggestions(workouts, sets, userProfile = {}) {
    const mlAnalyzer = await this.initializeMLAnalyzer(workouts, sets, userProfile);
    if (!mlAnalyzer) return [];

    try {
      const mlSuggestions = mlAnalyzer.generatePersonalizedSuggestions();
      return mlSuggestions.map(suggestion => ({
        ...suggestion,
        isMLBased: true
      }));
    } catch (error) {
      console.error('Errore nella generazione di suggerimenti ML:', error);
      return [];
    }
  }

  static generateTrainingReport(data) {
    const { workouts = [], sets = [], timeRange = 'month', debug = false, userProfile = {} } = data || {};
    const filteredWorkouts = this.filterWorkoutsByTimeRange(workouts, timeRange);
    
    // Calcola sempre le statistiche di base
    const workoutStats = calculateWorkoutStats(filteredWorkouts, timeRange, debug);
    
    // Assicurati che ci siano dati sufficienti prima di calcolare le distribuzioni
    const exerciseDistribution = sets.length > 0 
      ? analyzeExerciseDistribution(sets)
      : { mostCommonExercises: [], leastCommonExercises: [], totalExercises: 0 };
    
    // Calcola i progressi degli esercizi solo se ci sono abbastanza dati
    const exerciseProgress = sets.length > 2 
      ? this.analyzeExerciseProgress(sets)
      : {};
    
    const trainingSuggestions = this.generateTrainingSuggestions(filteredWorkouts, sets);
    const muscleBalanceSuggestions = this.generateMuscleBalanceSuggestions(sets);
    const muscleGroupAnalysis = analyzeMuscleTrained(sets);
    const periodizationSuggestions = this.generatePeriodizationSuggestions(filteredWorkouts, sets);

    return {
      workoutStats,
      exerciseDistribution,
      progress: exerciseProgress, // Cambiato da exerciseProgress
      trainingSuggestions,
      muscleBalanceSuggestions,
      periodizationSuggestions,
      muscleGroupAnalysis,
      hasMinimumData: workouts.length > 0 || sets.length > 0,
      // Assicuriamoci che le proprietà usate nella UI esistano sempre
      mostCommonExercises: exerciseDistribution.mostCommonExercises || [],
      leastCommonExercises: exerciseDistribution.leastCommonExercises || [],
      totalSets: sets.length
    };
  }

  static async generateTrainingReportWithML(data) {
    const { workouts = [], sets = [], timeRange = 'month', debug = false, userProfile = {} } = data || {};
    const baseReport = this.generateTrainingReport(data);
  
    if (workouts.length >= 10 && sets.length >= 50) {
      try {
        // Verifica stato ML
        const mlStatus = MLMonitor.checkMLStatus(workouts, sets);
        
        if (debug) {
          //debugLog('Stato ML:', mlStatus);
        }
        
        const mlAnalyzer = await this.initializeMLAnalyzer(workouts, sets, userProfile);
        if (mlAnalyzer) {
          // Utilizza MLDebugger per monitorare la generazione dei suggerimenti
          const monitorSuggestions = async () => {
            return mlAnalyzer.generatePersonalizedSuggestions();
          };
          
          const mlSuggestions = debug ? 
            await MLDebugger.monitorTrainingAnalysis(monitorSuggestions) : 
            mlAnalyzer.generatePersonalizedSuggestions();
            
          const workoutPatterns = mlAnalyzer.workoutPatterns;
          const userMetrics = mlAnalyzer.userMetrics;
  
          return {
            ...baseReport,
            mlAnalysis: {
              suggestions: mlSuggestions,
              workoutPatterns,
              userMetrics,
              dataQuality: mlStatus.dataQuality,
              confidenceLevel: mlStatus.estimatedAccuracy === 'alta' ? 'high' : 
                               mlStatus.estimatedAccuracy === 'media' ? 'medium' : 'low',
              mlStatus: debug ? mlStatus : undefined
            }
          };
        }
      } catch (error) {
        console.error('Errore nell\'analisi ML:', error);
      }
    }
  
    return {
      ...baseReport,
      mlAnalysis: {
        dataQuality: workouts.length < 10 ? 'insufficient' : 'limited',
        confidenceLevel: 'low',
        suggestions: [],
        workoutPatterns: [],
        userMetrics: {}
      }
    };
  }
  
  /**
   * Verifica lo stato del ML nell'applicazione
   * @param {Array} workouts - Allenamenti dell'utente
   * @param {Array} sets - Serie dell'utente 
   * @returns {Object} Stato e dettagli del ML
   */
  static checkMLStatus(workouts, sets) {
    return MLMonitor.checkMLStatus(workouts, sets);
  }

  /**
   * Esegue diagnostica completa sul sistema ML
   * @param {Array} workouts - Allenamenti dell'utente
   * @param {Array} sets - Serie dell'utente 
   * @returns {Promise<Object>} Risultati della diagnostica ML
   */
  static async runMLDiagnostics(workouts, sets, userProfile = {}) {
    const mlStatus = this.checkMLStatus(workouts, sets);
    
    if (!mlStatus.isActive) {
      return {
        isActive: false,
        mlStatus,
        message: 'Dati insufficienti per eseguire diagnostica ML'
      };
    }
    
    try {
      const mlAnalyzer = await this.initializeMLAnalyzer(workouts, sets, userProfile);
      if (!mlAnalyzer) {
        return {
          isActive: false,
          mlStatus,
          message: 'Inizializzazione MLAnalyzer fallita'
        };
      }
      
      // Esegui test sulle funzionalità ML
      const testResults = MLDebugger.runMLFeatureTests(mlAnalyzer);
      
      // Genera suggerimenti ML e standard per confronto
      const mlSuggestions = await this.generateMLSuggestions(workouts, sets, userProfile);
      const standardSuggestions = this.generateTrainingSuggestions(workouts, sets);
      
      return {
        isActive: true,
        mlStatus,
        testResults,
        suggestions: {
          ml: mlSuggestions.length,
          standard: standardSuggestions.length
        },
        mlAnalyzer
      };
    } catch (error) {
      console.error('Errore durante l\'esecuzione della diagnostica ML:', error);
      return {
        isActive: false,
        mlStatus,
        error: error.message
      };
    }
  }

  // Aggiungere questa nuova funzione a src/components/ai/TrainingAnalyzer.js

/**
 * Genera raccomandazioni dettagliate per il trainer
 * @param {Object} data - Dati di allenamento e profilo utente
 * @returns {Object} Analisi e raccomandazioni dettagliate
 */
static async generateTrainerRecommendations(data) {
  const { workouts = [], sets = [], userProfile = {}, currentAssignments = [] } = data || {};
  
  // Genera il report base
  const baseReport = this.generateTrainingReportWithML({
    workouts,
    sets,
    timeRange: 'all',
    userProfile
  });
  
  // Calcola scores personalizzati per il trainer
  const progressScore = this.calculateProgressScore(sets);
  const consistencyScore = this.calculateConsistencyScore(workouts);
  const techniqueScore = this.estimateTechniqueScore(sets);
  const balanceScore = this.calculateBalanceScore(sets);
  
  // Analisi complessiva per il trainer
  const trainerAnalysis = {
    progressScore,
    consistencyScore,
    techniqueScore,
    balanceScore,
    summary: this.generateSummaryForTrainer(
      progressScore, 
      consistencyScore, 
      techniqueScore, 
      balanceScore,
      userProfile
    )
  };
  
  // Genera raccomandazioni personalizzate per il trainer
  const trainerRecommendations = this.generateTrainerSpecificRecommendations(
    workouts,
    sets,
    userProfile,
    currentAssignments,
    {
      progressScore,
      consistencyScore,
      techniqueScore,
      balanceScore
    }
  );
  
  // Genera suggerimento di nuova scheda
  const workoutSuggestion = this.generateWorkoutSuggestion(
    workouts,
    sets,
    userProfile,
    currentAssignments
  );
  
  return {
    base: baseReport,
    analysis: trainerAnalysis,
    recommendations: trainerRecommendations,
    workoutSuggestion
  };
}

/**
 * Calcola un punteggio di progresso basato sui dati delle serie
 * @param {Array} sets - Serie di allenamento
 * @returns {number} Punteggio di progresso (0-10)
 */
static calculateProgressScore(sets) {
  if (!sets || sets.length < 10) return 5; // Valore default con dati insufficienti
  
  // Analizza il progresso per ogni esercizio
  const exerciseProgress = this.analyzeExerciseProgress(sets);
  
  // Calcola percentuale di esercizi con progresso positivo
  const exerciseEntries = Object.entries(exerciseProgress);
  if (exerciseEntries.length === 0) return 5;
  
  const positiveProgressCount = exerciseEntries.filter(
    ([_, data]) => parseFloat(data.weightChangePercent) > 0
  ).length;
  
  const progressPercentage = (positiveProgressCount / exerciseEntries.length) * 100;
  
  // Calcola valore medio di progresso percentuale
  const avgProgressPercentage = exerciseEntries.reduce(
    (sum, [_, data]) => sum + parseFloat(data.weightChangePercent), 
    0
  ) / exerciseEntries.length;
  
  // Combina i fattori per un punteggio complessivo
  // 60% basato sulla percentuale di esercizi con progresso positivo
  // 40% basato sul valore medio di progresso percentuale
  const progressPercentageScore = Math.min(10, progressPercentage / 10);
  const avgProgressScore = Math.min(10, avgProgressPercentage / 3);
  
  return Math.round((progressPercentageScore * 0.6) + (avgProgressScore * 0.4));
}

/**
 * Calcola un punteggio di costanza basato sui dati degli allenamenti
 * @param {Array} workouts - Allenamenti
 * @returns {number} Punteggio di costanza (0-10)
 */
static calculateConsistencyScore(workouts) {
  if (!workouts || workouts.length < 2) return 5; // Valore default con dati insufficienti
  
  // Ordina gli allenamenti per data
  const sortedWorkouts = [...workouts].sort(
    (a, b) => new Date(a.data_allenamento) - new Date(b.data_allenamento)
  );
  
  // Calcola intervalli tra allenamenti
  const intervals = [];
  for (let i = 1; i < sortedWorkouts.length; i++) {
    const currentDate = new Date(sortedWorkouts[i].data_allenamento);
    const prevDate = new Date(sortedWorkouts[i-1].data_allenamento);
    const diffDays = Math.round((currentDate - prevDate) / (1000 * 60 * 60 * 24));
    
    if (diffDays >= 1 && diffDays <= 14) { // Filtra valori anomali
      intervals.push(diffDays);
    }
  }
  
  if (intervals.length === 0) return 5;
  
  // Calcola media e deviazione standard
  const avgInterval = intervals.reduce((sum, val) => sum + val, 0) / intervals.length;
  const variance = intervals.reduce((sum, val) => sum + Math.pow(val - avgInterval, 2), 0) / intervals.length;
  const stdDev = Math.sqrt(variance);
  
  // Calcola frequenza settimanale
  const weeklyFrequency = 7 / avgInterval;
  
  // Punteggio basato sulla regolarità (deviazione standard bassa = alta costanza)
  const regularityScore = Math.max(0, 10 - Math.min(10, stdDev));
  
  // Punteggio basato sulla frequenza (ideale 3-5 volte/settimana)
  let frequencyScore = 0;
  if (weeklyFrequency >= 3 && weeklyFrequency <= 5) {
    frequencyScore = 10; // Ottimale
  } else if (weeklyFrequency > 5) {
    frequencyScore = Math.max(5, 10 - (weeklyFrequency - 5)); // Troppo frequente
  } else {
    frequencyScore = Math.max(0, weeklyFrequency * 2.5); // Poco frequente
  }
  
  // Combina i punteggi (70% regolarità, 30% frequenza)
  return Math.round((regularityScore * 0.7) + (frequencyScore * 0.3));
}

/**
 * Stima un punteggio di tecnica basato sui dati disponibili
 * @param {Array} sets - Serie di allenamento
 * @returns {number} Punteggio di tecnica stimato (0-10)
 */
static estimateTechniqueScore(sets) {
  if (!sets || sets.length < 10) return 5; // Valore default con dati insufficienti

  // Fattori che possono indicare una buona tecnica
  // 1. Progressione costante
  // 2. Intervalli di ripetizioni appropriati
  // 3. Variabilità nei carichi per lo stesso esercizio

  // Raggruppa per esercizio
  const exerciseSetsMap = {};
  sets.forEach(set => {
    const exerciseName = set.esercizio_nome;
    if (!exerciseName) return;

    if (!exerciseSetsMap[exerciseName]) {
      exerciseSetsMap[exerciseName] = [];
    }

    exerciseSetsMap[exerciseName].push({
      reps: parseInt(set.ripetizioni) || 0,
      weight: parseFloat(set.peso) || 0,
      date: new Date(set.timestamp || set.data_allenamento || new Date())
    });
  });

  // Valuta ciascun esercizio
  let totalScore = 0;
  let exerciseCount = 0;

  for (const [exerciseName, exerciseSetsForExercise] of Object.entries(exerciseSetsMap)) {
    if (exerciseSetsForExercise.length < 3) continue;

    // Ordina per data
    exerciseSetsForExercise.sort((a, b) => a.date - b.date);

    // 1. Verifica progressione costante
    const progressionScore = this.evaluateProgressionConsistency(exerciseSetsForExercise);

    // 2. Valuta range di ripetizioni
    const repRangeScore = this.evaluateRepetitionRanges(exerciseSetsForExercise);

    // 3. Valuta variabilità appropriata nei carichi
    const variabilityScore = this.evaluateWeightVariability(exerciseSetsForExercise);

    // Calcola punteggio complessivo per l'esercizio
    const exerciseScore = (progressionScore * 0.5) + (repRangeScore * 0.3) + (variabilityScore * 0.2);

    totalScore += exerciseScore;
    exerciseCount++;
  }

  return exerciseCount > 0 ? Math.round(totalScore / exerciseCount) : 5;
}

/**
 * Valuta la consistenza della progressione per un esercizio
 * @param {Array} exerciseSets - Serie di un esercizio
 * @returns {number} Punteggio (0-10)
 */
static evaluateProgressionConsistency(exerciseSets) {
  // Implementazione semplificata
  // Idealmente, la progressione dovrebbe essere graduale senza salti eccessivi
  
  // Estrai pesi ordinati per data
  const weightsByDate = [...exerciseSets]
    .sort((a, b) => a.date - b.date)
    .map(set => set.weight);
  
  // Calcola le variazioni di peso
  const weightChanges = [];
  for (let i = 1; i < weightsByDate.length; i++) {
    if (weightsByDate[i] > 0 && weightsByDate[i-1] > 0) {
      const changePercent = ((weightsByDate[i] - weightsByDate[i-1]) / weightsByDate[i-1]) * 100;
      weightChanges.push(changePercent);
    }
  }
  
  if (weightChanges.length === 0) return 5;
  
  // Valuta la variabilità delle variazioni (idealmente dovrebbero essere costanti)
  const avgChange = weightChanges.reduce((sum, change) => sum + change, 0) / weightChanges.length;
  const changeVariance = weightChanges.reduce((sum, change) => sum + Math.pow(change - avgChange, 2), 0) / weightChanges.length;
  
  // Un'alta varianza suggerisce inconsistenza (salti troppo grandi o regressioni)
  const consistencyScore = Math.max(0, 10 - Math.min(10, Math.sqrt(changeVariance)));
  
  return consistencyScore;
}

/**
 * Valuta l'appropriatezza dei range di ripetizioni
 * @param {Array} exerciseSets - Serie di un esercizio
 * @returns {number} Punteggio (0-10)
 */
static evaluateRepetitionRanges(exerciseSets) {
  const reps = exerciseSets.map(set => set.reps).filter(r => r > 0);
  
  if (reps.length === 0) return 5;
  
  // Calcola range e media
  const minReps = Math.min(...reps);
  const maxReps = Math.max(...reps);
  const avgReps = reps.reduce((sum, r) => sum + r, 0) / reps.length;
  
  // Valuta appropriatezza del range
  // Range 1-20 considerato appropriato, con focus su 6-12 per ipertrofia
  let rangeScore = 10;
  
  if (minReps < 1 || maxReps > 30) {
    rangeScore = 3; // Range non appropriato
  } else if (maxReps > 20) {
    rangeScore = 7; // Accettabile ma non ottimale
  }
  
  // Valuta focus su range efficaci (6-12 per ipertrofia, 1-5 per forza)
  const hypertrophyReps = reps.filter(r => r >= 6 && r <= 12).length;
  const strengthReps = reps.filter(r => r >= 1 && r <= 5).length;
  const focusScore = Math.min(10, ((hypertrophyReps + strengthReps) / reps.length) * 10);
  
  return (rangeScore * 0.7) + (focusScore * 0.3);
}

/**
 * Valuta l'appropriata variabilità nei carichi
 * @param {Array} exerciseSets - Serie di un esercizio
 * @returns {number} Punteggio (0-10)
 */
static evaluateWeightVariability(exerciseSets) {
  const weights = exerciseSets.map(set => set.weight).filter(w => w > 0);
  
  if (weights.length === 0) return 5;
  
  // Se tutti i pesi sono identici, potrebbe indicare mancanza di progressione
  const uniqueWeights = new Set(weights);
  if (uniqueWeights.size === 1) return 4;
  
  // Calcola coefficiente di variazione (CV = deviazione standard / media)
  const mean = weights.reduce((sum, w) => sum + w, 0) / weights.length;
  const variance = weights.reduce((sum, w) => sum + Math.pow(w - mean, 2), 0) / weights.length;
  const stdDev = Math.sqrt(variance);
  const cv = (stdDev / mean) * 100;
  
  // CV ideale tra 5% e 20% (troppo basso = poca progressione, troppo alto = inconsistenza)
  if (cv < 5) return 5; // Variabilità insufficiente
  if (cv > 30) return 6; // Variabilità eccessiva
  if (cv >= 5 && cv <= 20) return 10; // Variabilità ideale
  
  return 8; // Variabilità accettabile
}

/**
 * Calcola un punteggio di bilanciamento muscolare
 * @param {Array} sets - Serie di allenamento
 * @returns {number} Punteggio di bilanciamento (0-10)
 */
static calculateBalanceScore(sets) {
  if (!sets || sets.length < 10) return 5; // Valore default con dati insufficienti
  
  // Conta set per gruppo muscolare
  const muscleGroupCounts = {};
  sets.forEach(set => {
    const muscleGroup = set.gruppo_muscolare || 'Altro';
    muscleGroupCounts[muscleGroup] = (muscleGroupCounts[muscleGroup] || 0) + 1;
  });
  
  // Calcola il valore per i gruppi muscolari principali
  const mainMuscleGroups = ['Petto', 'Schiena', 'Gambe', 'Spalle', 'Braccia', 'Core'];
  const mainGroupsPresent = mainMuscleGroups.filter(group => muscleGroupCounts[group] > 0);
  
  // Se mancano gruppi principali, penalizza il punteggio
  const coverageScore = Math.min(10, (mainGroupsPresent.length / mainMuscleGroups.length) * 10);
  
  // Calcola il bilanciamento tra gruppi (meno varianza = più bilanciato)
  const presentGroups = Object.entries(muscleGroupCounts)
    .filter(([group]) => mainMuscleGroups.includes(group));
  
  if (presentGroups.length <= 1) return coverageScore;
  
  const counts = presentGroups.map(([_, count]) => count);
  const mean = counts.reduce((sum, count) => sum + count, 0) / counts.length;
  const variance = counts.reduce((sum, count) => sum + Math.pow(count - mean, 2), 0) / counts.length;
  const stdDev = Math.sqrt(variance);
  const cv = (stdDev / mean) * 100; // Coefficiente di variazione
  
  // Converti CV in punteggio (meno CV = maggiore bilanciamento)
  const balanceScore = Math.max(0, 10 - Math.min(10, cv / 10));
  
  // Combina i punteggi (70% copertura, 30% bilanciamento)
  return Math.round((coverageScore * 0.7) + (balanceScore * 0.3));
}

/**
 * Genera un sommario testuale per il trainer
 * @param {number} progressScore - Punteggio di progresso
 * @param {number} consistencyScore - Punteggio di costanza
 * @param {number} techniqueScore - Punteggio di tecnica
 * @param {number} balanceScore - Punteggio di bilanciamento
 * @param {Object} userProfile - Profilo dell'utente
 * @returns {string} Sommario testuale
 */
static generateSummaryForTrainer(
  progressScore,
  consistencyScore,
  techniqueScore,
  balanceScore,
  userProfile
) {
  const experienceLevel = userProfile?.experienceLevel || 'beginner';
  const fitnessGoals = userProfile?.fitnessGoals || 'general_fitness';
  
  // Valutazione complessiva
  const totalScore = (progressScore + consistencyScore + techniqueScore + balanceScore) / 4;
  
  let summary = '';
  
  // Introduzione basata sul livello di esperienza
  if (experienceLevel === 'beginner') {
    summary += `L'utente mostra un livello di allenamento tipico di un principiante. `;
  } else if (experienceLevel === 'intermediate') {
    summary += `L'utente dimostra un livello intermedio di allenamento. `;
  } else {
    summary += `L'utente mostra caratteristiche di un atleta avanzato. `;
  }
  
  // Punti di forza
  summary += `I suoi punti di forza includono `;
  
  const strengths = [];
  if (progressScore >= 7) strengths.push(`progressione costante (${progressScore}/10)`);
  if (consistencyScore >= 7) strengths.push(`costanza negli allenamenti (${consistencyScore}/10)`);
  if (techniqueScore >= 7) strengths.push(`buona tecnica di esecuzione stimata (${techniqueScore}/10)`);
  if (balanceScore >= 7) strengths.push(`bilanciamento muscolare (${balanceScore}/10)`);
  
  if (strengths.length === 0) {
    // Se non ci sono punti di forza evidenti
    summary += `la motivazione a migliorarsi, anche se i punteggi attuali suggeriscono ampi margini di miglioramento.`;
  } else if (strengths.length === 1) {
    summary += `${strengths[0]}.`;
  } else {
    summary += `${strengths.slice(0, -1).join(', ')} e ${strengths[strengths.length - 1]}.`;
  }
  
  // Aree di miglioramento
  const weaknesses = [];
  if (progressScore < 7) weaknesses.push(`progressione (${progressScore}/10)`);
  if (consistencyScore < 7) weaknesses.push(`costanza (${consistencyScore}/10)`);
  if (techniqueScore < 7) weaknesses.push(`tecnica di esecuzione (${techniqueScore}/10)`);
  if (balanceScore < 7) weaknesses.push(`bilanciamento muscolare (${balanceScore}/10)`);
  
  if (weaknesses.length > 0) {
    summary += ` Le aree che richiedono maggiore attenzione sono: `;
    
    if (weaknesses.length === 1) {
      summary += `${weaknesses[0]}.`;
    } else {
      summary += `${weaknesses.slice(0, -1).join(', ')} e ${weaknesses[weaknesses.length - 1]}.`;
    }
  }
  
  // Obiettivi
  summary += ` In base agli obiettivi dell'utente (`;
  
  if (fitnessGoals === 'general_fitness') {
    summary += `benessere generale), `;
  } else if (fitnessGoals === 'muscle_gain') {
    summary += `aumento massa muscolare), `;
  } else if (fitnessGoals === 'strength') {
    summary += `forza), `;
  } else if (fitnessGoals === 'weight_loss') {
    summary += `perdita di peso), `;
  } else {
    summary += `${fitnessGoals}), `;
  }
  
  // Conclusione e raccomandazione generale
  if (totalScore >= 8) {
    summary += `il programma attuale sta funzionando bene, ma può beneficiare di piccoli aggiustamenti per ottimizzare ulteriormente i risultati.`;
  } else if (totalScore >= 6) {
    summary += `il programma attuale sta dando risultati discreti, ma potrebbe beneficiare di alcune modifiche strutturali per migliorare l'efficacia.`;
  } else {
    summary += `è consigliabile una revisione significativa del programma attuale per allinearlo meglio agli obiettivi e migliorare i risultati.`;
  }
  
  return summary;
}

/**
 * Genera raccomandazioni specifiche per il trainer
 * @param {Array} workouts - Allenamenti
 * @param {Array} sets - Serie
 * @param {Object} userProfile - Profilo utente
 * @param {Array} currentAssignments - Schede attualmente assegnate
 * @param {Object} scores - Punteggi di valutazione
 * @returns {Array} Raccomandazioni per il trainer
 */
static generateTrainerSpecificRecommendations(
  workouts,
  sets,
  userProfile,
  currentAssignments,
  scores
) {
  const recommendations = [];
  
  // Raccomandazioni basate sul punteggio di progresso
  if (scores.progressScore < 6) {
    recommendations.push({
      title: "Revisione della progressione del carico",
      description: "L'utente mostra una progressione limitata nel carico. Considera di implementare un sistema di progressione più strutturato.",
      priority: "high",
      steps: [
        "Implementa un sistema di progressione lineare con incrementi del 2.5-5% settimanali per gli esercizi principali",
        "Utilizza micro-carichi (0.5-1 kg) per una progressione più graduale ma costante",
        "Introduce periodizzazione per alternare fasi di volume e intensità"
      ]
    });
  }
  
  // Raccomandazioni basate sul punteggio di costanza
  if (scores.consistencyScore < 6) {
    recommendations.push({
      title: "Miglioramento della costanza negli allenamenti",
      description: "L'utente mostra irregolarità nella frequenza degli allenamenti. Una routine più strutturata potrebbe migliorare i risultati.",
      priority: "medium",
      steps: [
        "Programma 3-4 allenamenti a settimana in giorni fissi per creare abitudine",
        "Crea un sistema di check-in settimanale per monitorare la compliance",
        "Considera un approccio più flessibile con allenamenti alternativi per giorni in cui non può allenarsi"
      ]
    });
  }
  
  // Raccomandazioni basate sul punteggio di tecnica
  if (scores.techniqueScore < 6) {
    recommendations.push({
      title: "Miglioramento della tecnica di esecuzione",
      description: "I pattern di allenamento suggeriscono potenziali problemi di tecnica. Considera di dedicare attenzione alla forma corretta.",
      priority: "high",
      steps: [
        "Programma una sessione di verifica tecnica per gli esercizi principali",
        "Aggiungi serie di riscaldamento con peso leggero per focalizzarsi sulla tecnica",
        "Rivaluta i carichi attuali, potenzialmente riducendoli temporaneamente per concentrarsi sulla forma"
      ]
    });
  }
  
  // Raccomandazioni basate sul punteggio di bilanciamento
  if (scores.balanceScore < 6) {
    // Analizza quali gruppi muscolari sono sottoallenati
    const muscleGroupCounts = {};
    sets.forEach(set => {
      const muscleGroup = set.gruppo_muscolare || 'Altro';
      muscleGroupCounts[muscleGroup] = (muscleGroupCounts[muscleGroup] || 0) + 1;
    });
    
    const entries = Object.entries(muscleGroupCounts);
    if (entries.length > 1) {
      entries.sort((a, b) => a[1] - b[1]); // Ordina per conteggio crescente
      const leastTrainedGroups = entries.slice(0, Math.min(2, Math.floor(entries.length / 2)))
        .map(([group]) => group);
      
      recommendations.push({
        title: "Miglioramento del bilanciamento muscolare",
        description: `L'utente mostra uno sbilanciamento nel volume di allenamento tra i diversi gruppi muscolari, con ${leastTrainedGroups.join(' e ')} particolarmente sottoallenati.`,
        priority: "medium",
        steps: [
          `Aumenta il volume per ${leastTrainedGroups.join(' e ')} aggiungendo 1-2 esercizi specifici per sessione`,
          "Implementa un approccio di suddivisione più equilibrato (es. push/pull/legs o upper/lower)",
          "Monitora il volume per gruppo muscolare nelle prossime 4-6 settimane per verificare i miglioramenti"
        ],
        exercises: this.getSuggestedExercisesForGroups(leastTrainedGroups)
      });
    }
  }
  
  // Valuta le schede attuali
  if (currentAssignments && currentAssignments.length > 0) {
    // Analizza le schede attualmente assegnate all'utente
    const currentWorkoutAnalysis = this.analyzeCurrentWorkouts(currentAssignments, userProfile);
    
    if (currentWorkoutAnalysis.issues.length > 0) {
      recommendations.push({
        title: "Ottimizzazione schede attuali",
        description: `Le schede attualmente assegnate presentano alcune aree di miglioramento: ${currentWorkoutAnalysis.issues.join('; ')}.`,
        priority: "medium",
        steps: currentWorkoutAnalysis.recommendations
      });
    }
  } else {
    // Se non ci sono schede assegnate
    recommendations.push({
      title: "Creazione nuova scheda",
      description: "L'utente non ha schede attualmente assegnate. È consigliabile creare una nuova scheda personalizzata in base all'analisi dei dati storici.",
      priority: "high",
      steps: [
        "Utilizza i suggerimenti della scheda generati dall'AI come punto di partenza",
        "Personalizza gli esercizi in base alle preferenze dell'utente",
        "Implementa un sistema di progressione chiaro per ciascun esercizio"
      ]
    });
  }
  
  // Aggiungi raccomandazioni specifiche per gli obiettivi dell'utente
  if (userProfile?.fitnessGoals) {
    recommendations.push(...this.getGoalSpecificRecommendations(userProfile.fitnessGoals, scores));
  }
  
  return recommendations;
}

/**
 * Ottiene esercizi suggeriti per gruppi muscolari specifici
 * @param {Array} muscleGroups - Gruppi muscolari
 * @returns {Array} Esercizi suggeriti
 */
static getSuggestedExercisesForGroups(muscleGroups) {
  const exercisesByGroup = {
    'Petto': ['Panca Piana', 'Panca Inclinata', 'Croci con Manubri', 'Dip'],
    'Schiena': ['Trazioni', 'Rematore', 'Pulley', 'Pullover'],
    'Gambe': ['Squat', 'Leg Press', 'Leg Extension', 'Leg Curl', 'Affondi'],
    'Spalle': ['Military Press', 'Alzate Laterali', 'Alzate Frontali', 'Rear Delt'],
    'Bicipiti': ['Curl con Bilanciere', 'Curl con Manubri', 'Curl Martello', 'Curl Concentrato'],
    'Tricipiti': ['French Press', 'Pushdown', 'Dip', 'Kick-back'],
    'Addominali': ['Crunch', 'Plank', 'Leg Raise', 'Russian Twist'],
    'Core': ['Plank', 'Leg Raise', 'Pallof Press', 'Side Plank']
  };
  
  const suggestedExercises = [];
  
  muscleGroups.forEach(group => {
    const exercises = exercisesByGroup[group] || [];
    if (exercises.length > 0) {
      suggestedExercises.push(...exercises.slice(0, 3));
    }
  });
  
  return suggestedExercises;
}

/**
 * Analizza le schede attualmente assegnate all'utente
 * @param {Array} workouts - Schede assegnate
 * @param {Object} userProfile - Profilo utente
 * @returns {Object} Analisi delle schede attuali
 */
static analyzeCurrentWorkouts(workouts, userProfile) {
  const issues = [];
  const recommendations = [];
  
  // Analisi di base
  if (workouts.length > 3) {
    issues.push('Troppe schede assegnate simultaneamente');
    recommendations.push('Riduci a massimo 2-3 schede attive per evitare confusione');
  }
  
  // Qui si potrebbero implementare analisi più sofisticate 
  // delle schede attualmente assegnate, ma richiederebbe
  // accesso ai dettagli completi delle schede
  
  // Per ora implementiamo un'analisi base generica
  issues.push('Mancano parametri di progressione chiari');
  recommendations.push('Definisci parametri di progressione specifici per ogni esercizio');
  
  recommendations.push('Aggiorna le schede in base agli ultimi progressi dell\'utente');
  
  return {
    issues,
    recommendations
  };
}

/**
 * Ottiene raccomandazioni specifiche per gli obiettivi dell'utente
 * @param {string} fitnessGoal - Obiettivo fitness dell'utente
 * @param {Object} scores - Punteggi di valutazione
 * @returns {Array} Raccomandazioni specifiche per l'obiettivo
 */
static getGoalSpecificRecommendations(fitnessGoal, scores) {
  const recommendations = [];
  
  switch (fitnessGoal) {
    case 'muscle_gain':
      recommendations.push({
        title: "Ottimizzazione per ipertrofia",
        description: "Per massimizzare la crescita muscolare, è importante focalizzarsi sul volume di allenamento e sulla nutrizione.",
        priority: "medium",
        steps: [
          "Focalizza l'allenamento su range di 8-12 ripetizioni per la maggior parte degli esercizi",
          "Implementa tecniche di intensificazione come serie discendenti e ripetizioni forzate",
          "Assicurati che l'utente mantenga un surplus calorico moderato con sufficiente apporto proteico"
        ]
      });
      break;
      
    case 'strength':
      recommendations.push({
        title: "Ottimizzazione per forza",
        description: "Per massimizzare i guadagni di forza, è importante focalizzarsi su esercizi composti con intensità elevate.",
        priority: "medium",
        steps: [
          "Struttura l'allenamento attorno ai 5 grandi esercizi: squat, stacco, panca, overhead press e trazioni",
          "Focalizza il lavoro su range di 1-6 ripetizioni con carichi elevati (80-95% 1RM)",
          "Implementa una periodizzazione per gestire efficacemente il volume e l'intensità"
        ],
        exercises: ["Squat", "Stacco", "Panca Piana", "Military Press", "Trazioni Ponderate"]
      });
      break;
      
    case 'weight_loss':
      recommendations.push({
        title: "Ottimizzazione per perdita di peso",
        description: "Per supportare la perdita di peso, è importante combinare efficacemente allenamento di forza e cardio.",
        priority: "medium",
        steps: [
          "Mantieni alto il metabolismo con allenamenti full-body o split upper/lower",
          "Aggiungi sessioni cardio HIIT (10-20 minuti) a fine allenamento o in giorni separati",
          "Focalizza su circuiti con brevi recuperi per massimizzare il dispendio calorico"
        ]
      });
      break;
      
    case 'endurance':
      recommendations.push({
        title: "Ottimizzazione per resistenza",
        description: "Per migliorare la resistenza muscolare, è importante lavorare con volumi elevati e recuperi ridotti.",
        priority: "medium",
        steps: [
          "Struttura allenamenti con alte ripetizioni (15-20) e recuperi brevi (30-60s)",
          "Implementa circuiti e superset per mantenere alta l'intensità",
          "Integra gradualmente allenamenti di resistenza specifica (corsa, nuoto, ciclismo)"
        ]
      });
      break;
      
    default: // general_fitness
      recommendations.push({
        title: "Ottimizzazione per fitness generale",
        description: "Per migliorare il fitness generale, è importante un approccio bilanciato che sviluppi tutte le componenti del fitness.",
        priority: "medium",
        steps: [
          "Alterna allenamenti di forza, resistenza e mobilità durante la settimana",
          "Utilizza una combinazione di range di ripetizioni (6-8, 8-12, 15-20) per sviluppare diverse qualità",
          "Implementa sessioni cardio di intensità variabile per migliorare la salute cardiovascolare"
        ]
      });
      break;
  }
  
  return recommendations;
}

/**
 * Genera un suggerimento di scheda basato sull'analisi dei dati
 * @param {Array} workouts - Allenamenti
 * @param {Array} sets - Serie
 * @param {Object} userProfile - Profilo utente
 * @param {Array} currentAssignments - Schede attualmente assegnate
 * @returns {Object} Suggerimento di scheda
 */
static generateWorkoutSuggestion(
  workouts,
  sets,
  userProfile,
  currentAssignments
) {
  // Determina il tipo di split più adatto
  const splitType = this.determineSplitType(workouts, sets, userProfile);
  
  // Genera la descrizione della scheda
  const description = this.generateWorkoutDescription(splitType, userProfile);
  
  // Crea lo split settimanale
  const weeklySplit = this.createWeeklySplit(splitType, userProfile);
  
  // Genera suggerimenti per esercizi specifici
  const exercises = this.suggestSpecificExercises(sets, userProfile, splitType);
  
  return {
    splitType,
    description,
    weeklySplit,
    exercises
  };
}

/**
 * Determina il tipo di split più adatto
 * @param {Array} workouts - Allenamenti
 * @param {Array} sets - Serie
 * @param {Object} userProfile - Profilo utente
 * @returns {string} Tipo di split consigliato
 */
static determineSplitType(workouts, sets, userProfile) {
  const experienceLevel = userProfile?.experienceLevel || 'beginner';
  const goal = userProfile?.fitnessGoals || 'general_fitness';
  
  // Analizza quanti giorni a settimana si allena l'utente
  const workoutsPerWeek = this.estimateWorkoutsPerWeek(workouts);
  
  // Logica di decisione del tipo di split
  if (experienceLevel === 'beginner' || workoutsPerWeek <= 2) {
    return 'fullbody';
  }
  
  if (workoutsPerWeek >= 5) {
    return 'bodypart';
  }
  
  if (workoutsPerWeek === 3) {
    return 'push_pull_legs';
  }
  
  if (workoutsPerWeek === 4) {
    return 'upper_lower';
  }
  
  // Default per casi non coperti
  return 'push_pull_legs';
}

/**
 * Stima il numero di allenamenti per settimana
 * @param {Array} workouts - Allenamenti
 * @returns {number} Numero stimato di allenamenti per settimana
 */
static estimateWorkoutsPerWeek(workouts) {
  if (!workouts || workouts.length < 2) return 3; // Default per dati insufficienti
  
  // Ordina gli allenamenti per data
  const sortedWorkouts = [...workouts].sort(
    (a, b) => new Date(a.data_allenamento) - new Date(b.data_allenamento)
  );
  
  // Calcola il periodo totale in settimane
  const firstDate = new Date(sortedWorkouts[0].data_allenamento);
  const lastDate = new Date(sortedWorkouts[sortedWorkouts.length - 1].data_allenamento);
  const weeksDiff = Math.max(1, Math.round((lastDate - firstDate) / (7 * 24 * 60 * 60 * 1000)));
  
  // Calcola media di allenamenti per settimana
  const weeklyAverage = workouts.length / weeksDiff;
  
  // Arrotonda al numero intero più vicino
  return Math.round(weeklyAverage);
}

/**
 * Genera la descrizione della scheda
 * @param {string} splitType - Tipo di split
 * @param {Object} userProfile - Profilo utente
 * @returns {string} Descrizione della scheda
 */
static generateWorkoutDescription(splitType, userProfile) {
  const experienceLevel = userProfile?.experienceLevel || 'beginner';
  const goal = userProfile?.fitnessGoals || 'general_fitness';
  
  // Base della descrizione
  let description = "Questa scheda è stata progettata specificamente ";
  
  // Personalizza in base al livello di esperienza
  if (experienceLevel === 'beginner') {
    description += "per un principiante, ";
  } else if (experienceLevel === 'intermediate') {
    description += "per un atleta di livello intermedio, ";
  } else {
    description += "per un atleta avanzato, ";
  }
  
  // Aggiungi informazioni sugli obiettivi
  if (goal === 'muscle_gain') {
    description += "con focus sull'ipertrofia muscolare. ";
  } else if (goal === 'strength') {
    description += "con focus sullo sviluppo della forza. ";
  } else if (goal === 'weight_loss') {
    description += "con focus sulla perdita di peso e tonificazione. ";
  } else if (goal === 'endurance') {
    description += "con focus sul miglioramento della resistenza. ";
  } else {
    description += "con un approccio bilanciato per il fitness generale. ";
  }
  
  // Aggiungi informazioni sul tipo di split
  if (splitType === 'fullbody') {
    description += "La struttura Full Body permette di allenare tutti i gruppi muscolari principali in ogni sessione, ottimizzando la frequenza di stimolazione e il recupero.";
  } else if (splitType === 'push_pull_legs') {
    description += "La struttura Push/Pull/Legs suddivide l'allenamento in giornate dedicate rispettivamente ai muscoli che spingono, tirano e alle gambe, permettendo un recupero ottimale.";
  } else if (splitType === 'upper_lower') {
    description += "La struttura Upper/Lower alterna allenamenti dedicati alla parte superiore e inferiore del corpo, offrendo un buon equilibrio tra volume, intensità e recupero.";
  } else if (splitType === 'bodypart') {
    description += "La struttura a suddivisione muscolare permette di dedicare sessioni specifiche a gruppi muscolari singoli, massimizzando il volume e l'intensità per ciascun gruppo.";
  }
  
  return description;
}

/**
 * Crea lo split settimanale
 * @param {string} splitType - Tipo di split
 * @param {Object} userProfile - Profilo utente
 * @returns {Array} Split settimanale
 */
static createWeeklySplit(splitType, userProfile) {
  const split = [];
  
  switch (splitType) {
    case 'fullbody':
      split.push(
        { focus: 'Full Body A', exercises: ['Squat', 'Panca Piana', 'Rematore', 'Military Press', 'Curl'] },
        { focus: 'Full Body B', exercises: ['Stacco', 'Panca Inclinata', 'Trazioni', 'Alzate Laterali', 'Dip'] },
        { focus: 'Full Body C', exercises: ['Affondi', 'Croci', 'Pulley', 'Alzate Frontali', 'French Press'] }
      );
      break;
      
    case 'push_pull_legs':
      split.push(
        { focus: 'Push', exercises: ['Panca Piana', 'Panca Inclinata', 'Military Press', 'Alzate Laterali', 'Dip'] },
        { focus: 'Pull', exercises: ['Stacco', 'Trazioni', 'Rematore', 'Pulley', 'Curl con Bilanciere'] },
        { focus: 'Legs', exercises: ['Squat', 'Leg Press', 'Leg Extension', 'Leg Curl', 'Polpacci'] }
      );
      break;
      
    case 'upper_lower':
      split.push(
        { focus: 'Upper A', exercises: ['Panca Piana', 'Rematore', 'Military Press', 'Trazioni', 'Dip'] },
        { focus: 'Lower A', exercises: ['Squat', 'Leg Press', 'Leg Extension', 'Leg Curl', 'Polpacci'] },
        { focus: 'Upper B', exercises: ['Panca Inclinata', 'Pulley', 'Alzate Laterali', 'Curl', 'French Press'] },
        { focus: 'Lower B', exercises: ['Stacco', 'Affondi', 'Leg Curl', 'Abduttori', 'Crunch'] }
      );
      break;
      
    case 'bodypart':
      split.push(
        { focus: 'Petto', exercises: ['Panca Piana', 'Panca Inclinata', 'Croci', 'Pectoral Machine', 'Dip'] },
        { focus: 'Schiena', exercises: ['Trazioni', 'Rematore', 'Pulley', 'Pullover', 'Shrug'] },
        { focus: 'Gambe', exercises: ['Squat', 'Leg Press', 'Leg Extension', 'Leg Curl', 'Polpacci'] },
        { focus: 'Spalle', exercises: ['Military Press', 'Alzate Laterali', 'Alzate Frontali', 'Rear Delt', 'Shrug'] },
        { focus: 'Braccia', exercises: ['Curl con Bilanciere', 'Curl con Manubri', 'French Press', 'Pushdown', 'Hammer Curl'] }
      );
      break;
      
    default:
      // Default fallback 
      split.push(
        { focus: 'Allenamento A', exercises: ['Squat', 'Panca Piana', 'Rematore', 'Military Press'] },
        { focus: 'Allenamento B', exercises: ['Stacco', 'Panca Inclinata', 'Trazioni', 'Alzate Laterali'] },
        { focus: 'Allenamento C', exercises: ['Leg Press', 'Dip', 'Pulley', 'Curl'] }
      );
      break;
  }
  
  return split;
}

/**
 * Suggerisce esercizi specifici
 * @param {Array} sets - Serie
 * @param {Object} userProfile - Profilo utente
 * @param {string} splitType - Tipo di split
 * @returns {Array} Esercizi suggeriti
 */
static suggestSpecificExercises(sets, userProfile, splitType) {
  const experienceLevel = userProfile?.experienceLevel || 'beginner';
  const goal = userProfile?.fitnessGoals || 'general_fitness';
  
  // Analizza gli esercizi più utilizzati dall'utente
  const exerciseFrequency = {};
  sets.forEach(set => {
    const exerciseName = set.esercizio_nome;
    if (!exerciseName) return;
    
    exerciseFrequency[exerciseName] = (exerciseFrequency[exerciseName] || 0) + 1;
  });
  
  // Converti in array e ordina per frequenza
  const sortedExercises = Object.entries(exerciseFrequency)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
  
  // Seleziona i migliori esercizi per l'utente
  const favoriteExercises = sortedExercises.slice(0, 8).map(ex => ex.name);
  
  // Crea una lista di esercizi consigliati
  const suggestedExercises = [];
  
  // Esercizi base che dovrebbero essere inclusi (in base al livello e obiettivo)
  const baseExercises = [];
  
  // Aggiungi esercizi composti base
  baseExercises.push(
    {
      name: 'Squat',
      muscleGroup: 'Gambe',
      sets: experienceLevel === 'beginner' ? 3 : experienceLevel === 'intermediate' ? 4 : 5,
      reps: goal === 'strength' ? '5' : goal === 'muscle_gain' ? '8-10' : '10-12',
      suggestedWeight: null
    },
    {
      name: 'Panca Piana',
      muscleGroup: 'Petto',
      sets: experienceLevel === 'beginner' ? 3 : experienceLevel === 'intermediate' ? 4 : 5,
      reps: goal === 'strength' ? '5' : goal === 'muscle_gain' ? '8-10' : '10-12',
      suggestedWeight: null
    },
    {
      name: 'Stacco',
      muscleGroup: 'Schiena',
      sets: experienceLevel === 'beginner' ? 3 : 4,
      reps: goal === 'strength' ? '5' : '8-10',
      suggestedWeight: null
    }
  );
  
  // Aggiungi alcuni degli esercizi preferiti dell'utente che non sono già inclusi nei base
  favoriteExercises.forEach(ex => {
    if (!baseExercises.some(baseEx => baseEx.name === ex)) {
      // Determina gruppo muscolare
      let muscleGroup = 'Altro';
      if (ex.toLowerCase().includes('panca') || ex.toLowerCase().includes('croci')) {
        muscleGroup = 'Petto';
      } else if (ex.toLowerCase().includes('squat') || ex.toLowerCase().includes('leg')) {
        muscleGroup = 'Gambe';
      } else if (ex.toLowerCase().includes('trazioni') || ex.toLowerCase().includes('rematore')) {
        muscleGroup = 'Schiena';
      } else if (ex.toLowerCase().includes('alzate') || ex.toLowerCase().includes('press')) {
        muscleGroup = 'Spalle';
      } else if (ex.toLowerCase().includes('curl')) {
        muscleGroup = 'Bicipiti';
      } else if (ex.toLowerCase().includes('dip') || ex.toLowerCase().includes('french')) {
        muscleGroup = 'Tricipiti';
      }
      
      suggestedExercises.push({
        name: ex,
        muscleGroup,
        sets: experienceLevel === 'beginner' ? 3 : 4,
        reps: goal === 'strength' ? '6-8' : goal === 'muscle_gain' ? '8-12' : '12-15',
        suggestedWeight: null
      });
    }
  });
  
  // Assicurati di avere un numero adeguato di esercizi
  return [...baseExercises, ...suggestedExercises.slice(0, 7)];
}
}

export default TrainingAnalyzer;